import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Image as ImageIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Slideshow as SlideshowIcon,
  Label,
  Upload as UploadIcon,
  SlideshowOutlined,
} from '@mui/icons-material';
import { usePptxFiles } from '../hooks/usePptxFiles';
import { uploadThumbnail, generatePptxDescription, updatePptxFile } from '../services/pptxFiles';
import SlideshowCard from '../components/pptx/SlideshowCard';
import type { PptxFile } from '../types';
import ThumbnailUploadDialog from '../components/pptx/ThumbnailUploadDialog';
import { useLang } from '../contexts/langContext';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../hooks/useAuth';
import TagFilterChips from '../components/common/TagFilterChips';
import { useTags } from '../hooks/useTags';
import ManageTagsDialog from '../components/common/ManageTagsDialog';
import EmptyState from '../components/common/EmptyState';
import axios from 'axios';

function FileUploadButton({ size = 'medium', onUpload }: { size?: 'small' | 'medium' | 'large', onUpload: (file: File) => Promise<void> }) {
  const { lang } = useLang();

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await onUpload(file);
    }
  }, [onUpload]);

  return (
    <Button
      variant="contained"
      component="label"
      color="primary"
      startIcon={<UploadIcon />}
      size={size}
      sx={{ flexGrow: 0, flexShrink: 0 }}
    >
      {lang === 'en' ? 'Upload PPTX' : '上传 PPTX'}
      <input
        type="file"
        hidden
        accept=".pptx"
        onChange={handleFileChange}
      />
    </Button>
  );
}

function PptxFilesPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTagsParam = searchParams.get('tags');
  const initialSelectedTagIds = initialTagsParam ? initialTagsParam.split(',').filter(Boolean) : [];
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialSelectedTagIds);
  const {
    pptxFiles,
    loading,
    error,
    handleUploadPptxFile,
    handleUpdatePptxFile,
    handleDeletePptxFile,
    handlePublishPptxFile,
    refetch,
  } = usePptxFiles();
  const { tags } = useTags();
  const [tagCounts, setTagCounts] = useState<Record<string | 'all', number> | undefined>(undefined);

  const [warningMessage, setWarningMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingPptx, setUploadingPptx] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    fileId: string;
  } | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [generatingDescription, setGeneratingDescription] = useState<Record<string, boolean>>({});
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);
  const [selectedFileForTags, setSelectedFileForTags] = useState<PptxFile | null>(null);

  const { setAppTitle } = useAppContext();
  useEffect(() => {
    setAppTitle(lang === 'en' ? 'PPTX Templates' : 'PPTX 模板');
  }, []);

  // Calculate tag counts whenever pptxFiles change
  useEffect(() => {
    if (pptxFiles.length > 0) {
      const counts: Record<string | 'all', number> = { all: pptxFiles.length };
      pptxFiles.forEach(file => {
        (file.tagIds || []).forEach(tagId => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      setTagCounts(counts);
    } else {
      setTagCounts({ all: 0 });
    }
  }, [pptxFiles]);

  // Sync tag selection to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedTagIds.length === 0) {
      params.delete('tags');
    } else {
      params.set('tags', selectedTagIds.join(','));
    }
    setSearchParams(params, { replace: true });
  }, [selectedTagIds]);

  // Filter files for display
  const displayedFiles = useMemo(() => {
    if (selectedTagIds.length === 0) return pptxFiles;
    return pptxFiles.filter(f => selectedTagIds.some(tid => f.tagIds?.includes(tid)));
  }, [pptxFiles, selectedTagIds]);

  const handleCloseWarning = useCallback(() => {
    setWarningMessage('');
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const handleUpload = useCallback(async (file: File) => {
    try {
      setUploadingPptx(true);
      await handleUploadPptxFile(file);
      setSuccessMessage(lang === 'en' ? 'Presentation uploaded successfully' : '演示文稿上传成功');
    } catch (err) {
      console.error('Failed to upload file:', err);
      let message: string;
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        message = err.response.data.message as string;
      } else if (err instanceof Error && err.message) {
        message = err.message;
      } else {
        message = lang === 'en' ? 'Failed to upload presentation' : '上传演示文稿失败';
      }
      setWarningMessage(message);
    } finally {
      setUploadingPptx(false);
    }
  }, [handleUploadPptxFile, lang]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await handleDeletePptxFile(id);
      setSuccessMessage(lang === 'en' ? 'Presentation deleted successfully' : '演示文稿已成功删除');
    } catch (err) {
      console.error('Failed to delete file:', err);
      setWarningMessage(lang === 'en' ? 'Failed to delete presentation' : '删除演示文稿失败');
    } finally {
      setFileToDelete(null);
    }
  }, [handleDeletePptxFile, lang]);

  const handlePublish = useCallback(async (id: string) => {
    try {
      await handlePublishPptxFile(id);
    } catch (err) {
      console.error('Failed to publish file:', err);
    }
  }, [handlePublishPptxFile]);

  const handleOpenUploadDialog = useCallback((fileId: string) => {
    setSelectedFileId(fileId);
    setUploadDialogOpen(true);
  }, []);

  const handleCloseUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
    setSelectedFileId(null);
  }, []);

  const handleThumbnailsUpload = useCallback(async (files: File[]) => {
    if (!selectedFileId) return;
    
    try {
      // Upload thumbnails sequentially
      for (let i = 0; i < files.length; i++) {
        const slideNumber = i + 1;
        await uploadThumbnail(selectedFileId, slideNumber, files[i]);
      }
      
      setSuccessMessage(lang === 'en' ? 'Thumbnails uploaded successfully' : '缩略图上传成功');
      await refetch();
    } catch (error) {
      console.error('Error uploading thumbnails:', error);
      setWarningMessage(lang === 'en' ? 'Failed to upload thumbnails. Please try again.' : '缩略图上传失败，请重试。');
    }
  }, [selectedFileId, refetch, lang]);

  const handleContextMenu = useCallback((event: React.MouseEvent, fileId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      fileId,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleMenuAction = useCallback((action: (id: string) => void, fileId: string) => {
    action(fileId);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  // Helper function to check if a file has thumbnails - since we have no direct way
  // to check, we'll use a custom approach to determine this
  const [missingThumbnails, setMissingThumbnails] = useState<Record<string, boolean>>({});

  // Check thumbnails existence by trying to load them
  useEffect(() => {
    if (!pptxFiles.length) return;

    const checkThumbnailsExistence = async () => {
      const results: Record<string, boolean> = {};
      
      for (const file of pptxFiles) {
        if (file.parsedSlides && file.parsedSlides.length > 0) {
          // Check first slide thumbnail as a representative check
          const slideNumber = 1;
          // This path matches what's used in SlideshowCard.tsx
          const thumbnailUrl = `/api/files/thumbnails/${file._id}/${slideNumber}.jpg`;
          
          try {
            const response = await fetch(thumbnailUrl, { method: 'HEAD' });
            results[file._id] = response.ok;
          } catch (error) {
            results[file._id] = false;
          }
        } else {
          results[file._id] = true; // No slides, so no thumbnails needed
        }
      }
      
      setMissingThumbnails(results);
    };
    
    checkThumbnailsExistence();
  }, [pptxFiles]);
  
  // Get files without thumbnails
  const filesWithoutThumbnails = pptxFiles.filter(file => 
    file.parsedSlides?.length > 0 && missingThumbnails[file._id] === false
  );

  // Get files without descriptions
  const filesWithoutDescriptions = pptxFiles.filter(file => file.createdBy === user?._id).filter(file => 
    !file.description || file.description.trim() === ''
  );

  // Get files with slides missing descriptions
  const filesWithSlidesWithoutDescriptions = pptxFiles.filter(file => file.createdBy === user?._id).filter(file => 
    file.parsedSlides?.some(slide => !slide.description || slide.description.trim() === '')
  );

  // Handler for generating a description for a specific file
  const handleGenerateDescription = useCallback(async (fileId: string) => {
    try {
      setGeneratingDescription(prev => ({ ...prev, [fileId]: true }));
      
      // Call the API to generate a description for this file
      const response = await generatePptxDescription(fileId, { lang });
      
      // Update the file with the generated description
      await updatePptxFile(fileId, { description: response.description });
      
      // Show success message
      setSuccessMessage(lang === 'en' ? 'Description generated successfully' : '描述已成功生成');
      
      // Refresh the file list
      await refetch();
    } catch (error) {
      console.error('Error generating description:', error);
      setWarningMessage(lang === 'en' ? 'Failed to generate description. Please try again.' : '生成描述失败，请重试。');
    } finally {
      setGeneratingDescription(prev => ({ ...prev, [fileId]: false }));
    }
  }, [lang, refetch]);

  const handleOpenHelpDialog = useCallback(() => {
    setHelpDialogOpen(true);
  }, []);

  const handleCloseHelpDialog = useCallback(() => {
    setHelpDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (fileToDelete) {
      handleDelete(fileToDelete);
    }
    setDeleteConfirmOpen(false);
  }, [fileToDelete, handleDelete]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  }, []);

  const handleOpenDeleteConfirm = useCallback((id: string) => {
    setFileToDelete(id);
    setDeleteConfirmOpen(true);
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleOpenManageTags = useCallback((fileId: string) => {
    const file = pptxFiles.find(f => f._id === fileId);
    if (file) {
      setSelectedFileForTags(file);
      setManageTagsDialogOpen(true);
    }
    handleCloseContextMenu();
  }, [pptxFiles, handleCloseContextMenu]);

  const handleTagsSaved = async (newTagIds: string[]) => {
    if (selectedFileForTags) {
      try {
        await handleUpdatePptxFile(selectedFileForTags._id, { tagIds: newTagIds });
        // Tag counts will be updated automatically through the refetch that happens in the hook
      } catch (error) {
        console.error('Failed to update tags:', error);
        setWarningMessage(lang === 'en' ? 'Failed to update tags' : '更新标签失败');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error">{error.message}</Typography>
      </Box>
    );
  }

  const empty = pptxFiles.length === 0 && !loading;

  if (empty) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <EmptyState
          imageURL={`/images/slide-show-empty-state.png`}
          message={lang === 'en' ? "No PPTX files found. Please upload PPTX files first to create projects." : "未找到模板文件。请先上传 PPTX 格式的模板文件才能创建项目。"}
          sx={{ py: 0 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <FileUploadButton size="large" onUpload={handleUpload} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* Tag Filter Chips */}
        {tags.length > 0 && (
          <TagFilterChips
            tags={tags}
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
            tagCounts={tagCounts}
          />
        )}

        <FileUploadButton size="medium" onUpload={handleUpload} />
      </Box>

      {filesWithoutThumbnails.length > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            p: 2, 
            // backgroundColor: 'warning.light', 
            border: '1px solid #ffa726',
            display: 'flex', 
            alignItems: 'center',
            borderRadius: 1,
          }}
        >
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {lang === 'en' 
                  ? `Missing thumbnails` 
                  : `缺少缩略图`}
              </Typography>
              <IconButton
                size="small"
                onClick={handleOpenHelpDialog}
                sx={{ ml: 0.5 }}
                aria-label={lang === 'en' ? 'Thumbnail export help' : '缩略图导出帮助'}
              >
                <HelpIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2">
              {lang === 'en' 
                ? `${filesWithoutThumbnails.length} presentation${filesWithoutThumbnails.length > 1 ? 's' : ''} need${filesWithoutThumbnails.length === 1 ? 's' : ''} thumbnails. Please use the context menu to upload them.` 
                : `${filesWithoutThumbnails.length} 个演示文稿需要缩略图。请使用上下文菜单上传它们。`}
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            color="warning"
            size="small"
            onClick={() => {
              const firstFileId = filesWithoutThumbnails[0]._id;
              handleOpenUploadDialog(firstFileId);
            }}
          >
            {lang === 'en' ? 'Upload Now' : '立即上传'}
          </Button>
        </Paper>
      )}

      {/* Files with slides missing descriptions */}
      <Stack spacing={2} sx={{ mb: filesWithSlidesWithoutDescriptions.length > 0 ? 3 : 0 }}>
        {filesWithSlidesWithoutDescriptions.filter(file => displayedFiles.map(f => f._id).includes(file._id)).map(file => {
          // Count slides without descriptions
          const totalSlides = file.parsedSlides?.length || 0;
          const slidesWithoutDesc = file.parsedSlides?.filter(
            slide => !slide.description || slide.description.trim() === ''
          ).length || 0;
          
          return (
            <Paper 
              key={`slides-no-desc-${file._id}`}
              elevation={0} 
              sx={{ 
                p: 2, 
                border: '1px solid #7b1fa2', // Purple color
                display: 'flex', 
                alignItems: 'center',
                borderRadius: 1,
              }}
            >
              <SlideshowIcon color="secondary" sx={{ mr: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {lang === 'en' 
                    ? `Slides missing descriptions` 
                    : `幻灯片缺少描述`}
                </Typography>
                <Typography variant="body2">
                  {lang === 'en' 
                    ? `"${file.filename}" has ${slidesWithoutDesc} out of ${totalSlides} slide${totalSlides > 1 ? 's' : ''} without descriptions. Complete slide descriptions improve search results.` 
                    : `"${file.filename}" 有 ${slidesWithoutDesc} 张（共 ${totalSlides} 张）幻灯片缺少描述。完整的幻灯片描述可以改善 AI 推荐结果。`}
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                color="secondary"
                size="small"
                sx={{ flexGrow: 0, flexShrink: 0 }}
                onClick={() => navigate(`/pptx-files/${file._id}`)}
              >
                {lang === 'en' ? 'View Details' : '查看详情'}
              </Button>
            </Paper>
          );
        })}
      </Stack>

      {/* Missing descriptions alerts */}
      <Stack spacing={2} sx={{ mb: filesWithoutDescriptions.length > 0 ? 3 : 0 }}>
        {filesWithoutDescriptions.filter(file => displayedFiles.map(f => f._id).includes(file._id)).map(file => (
          <Paper 
            key={`no-desc-${file._id}`}
            elevation={0} 
            sx={{ 
              p: 2, 
              border: '1px solid #3f51b5',
              display: 'flex', 
              alignItems: 'center',
              borderRadius: 1,
            }}
          >
            <DescriptionIcon color="primary" sx={{ mr: 1 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {lang === 'en' 
                  ? `Missing description` 
                  : `缺少描述`}
              </Typography>
              <Typography variant="body2">
                {lang === 'en' 
                  ? `"${file.filename}" has no description. A good description helps users understand this template.` 
                  : `"${file.filename}" 没有描述。良好的描述有助于用户理解此模板。`}
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="primary"
              size="small"
              onClick={() => handleGenerateDescription(file._id)}
              disabled={!!generatingDescription[file._id]}
              startIcon={generatingDescription[file._id] ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ flexGrow: 0, flexShrink: 0 }}
            >
              {generatingDescription[file._id]
                ? (lang === 'en' ? 'Generating...' : '正在生成...')
                : (lang === 'en' ? 'Generate Description' : '生成描述')}
            </Button>
          </Paper>
        ))}
      </Stack>

      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={handleCloseHelpDialog}
        aria-labelledby="thumbnail-help-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="thumbnail-help-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {lang === 'en' ? 'How to Export Thumbnails from PowerPoint' : '如何从 PowerPoint 导出缩略图'}
            <IconButton
              aria-label="close"
              onClick={handleCloseHelpDialog}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            {lang === 'en' ? 'Export slides as JPG images' : '将幻灯片导出为JPG图像'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '1. Open your presentation in PowerPoint'
              : '1. 在PowerPoint中打开您的演示文稿'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '2. Go to File > Save As'
              : '2. 转到 文件 > 另存为'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '3. In the "Save as type" dropdown, select "JPEG File Interchange Format (*.jpg)"'
              : '3. 在"保存类型"下拉菜单中，选择"JPEG文件交换格式(*.jpg)"'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '4. Click Save. PowerPoint will ask if you want to export all slides or just the current slide. Choose "All Slides"'
              : '4. 点击保存。PowerPoint会询问您是要导出所有幻灯片还是仅导出当前幻灯片。选择"所有幻灯片"'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '5. PowerPoint will create a folder with each slide saved as a separate JPG file'
              : '5. PowerPoint将创建一个文件夹，每张幻灯片都保存为单独的JPG文件'}
          </Typography>
          <Typography variant="body1" paragraph>
            {lang === 'en' 
              ? '6. If needed, resize the images to 640x360 pixels for optimal display'
              : '6. 如有需要，将图像调整为640x360像素以获得最佳显示效果'}
          </Typography>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {lang === 'en' ? 'Recommended settings:' : '推荐设置：'}
            </Typography>
            <Typography variant="body2">
              {lang === 'en' 
                ? '• Resolution: 640x360 pixels (16:9 aspect ratio)'
                : '• 分辨率：640x360像素（16:9宽高比）'}
            </Typography>
            <Typography variant="body2">
              {lang === 'en' 
                ? '• File format: JPG'
                : '• 文件格式：JPG'}
            </Typography>
            <Typography variant="body2">
              {lang === 'en' 
                ? '• File naming: Ensure files are named with sequential numbers matching slide numbers (1.jpg, 2.jpg, etc.)'
                : '• 文件命名：确保文件使用与幻灯片编号匹配的序号命名（1.jpg、2.jpg等）'}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ mt: 3 }} fontWeight="medium">
            {lang === 'en' 
              ? 'Note: Make sure to name the exported image files with sequential numbers matching the slide numbers (e.g., 1.jpg, 2.jpg, etc.) before uploading.'
              : '注意：在上传前，请确保将导出的图像文件用与幻灯片编号匹配的顺序数字命名（例如：1.jpg、2.jpg等）。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHelpDialog} variant="contained">
            {lang === 'en' ? 'Got it' : '明白了'}
          </Button>
        </DialogActions>
      </Dialog>

      {displayedFiles.length > 0 ? (
        <Grid container spacing={3}>
          {displayedFiles.map((file) => (
            <Grid item xs={12} sm={6} md={4} key={file._id}>
              <SlideshowCard
                file={file}
                tagNames={(file.tagIds || []).map(tid => {
                  const tag = tags.find(t => t._id === tid);
                  return tag ? tag.name : tid;
                })}
                onClick={() => navigate(`/pptx-files/${file._id}`)}
                onContextMenu={(e) => handleContextMenu(e, file._id)}
                onMoreClick={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, file._id);
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          icon={SlideshowOutlined}
          message={lang === 'en' ? "No PPTX files matching the filter." : "没有找到符合条件的模板文件。"}
          sx={{ py: 0 }}
        />
      )}

      <Menu
        open={!!contextMenu}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu && [
          <MenuItem
            key="manageTags"
            onClick={() => handleOpenManageTags(contextMenu.fileId)}
            disabled={!user || pptxFiles.find(f => f._id === contextMenu.fileId)?.createdBy !== user?._id}
          >
            <ListItemIcon>
              <Label fontSize="small" />
            </ListItemIcon>
            <ListItemText>{lang === 'en' ? 'Manage Tags' : '标签'}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="publish"
            onClick={() => handleMenuAction(handlePublish, contextMenu.fileId)}
            disabled={pptxFiles.find(f => f._id === contextMenu.fileId)?.public}
          >
            <ListItemIcon>
              <PublishIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{lang === 'en' ? 'Publish presentation' : '发布演示文稿'}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="thumbnails"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenUploadDialog(contextMenu.fileId);
              handleCloseContextMenu();
            }}
            disabled={isUploading || !user || pptxFiles.find(f => f._id === contextMenu.fileId)?.createdBy !== user?._id}
          >
            <ListItemIcon>
              {isUploading ? (
                <CircularProgress size={20} />
              ) : (
                <ImageIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{lang === 'en' ? 'Upload thumbnails' : '上传缩略图'}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => handleOpenDeleteConfirm(contextMenu.fileId)}
            disabled={!user || pptxFiles.find(f => f._id === contextMenu.fileId)?.createdBy !== user?._id}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{lang === 'en' ? 'Delete presentation' : '删除演示文稿'}</ListItemText>
          </MenuItem>
        ]}
      </Menu>

      <ManageTagsDialog
        open={manageTagsDialogOpen}
        onClose={() => setManageTagsDialogOpen(false)}
        entityType="pptx"
        entity={selectedFileForTags}
        onSaved={handleTagsSaved}
      />

      {selectedFileId && (
        <ThumbnailUploadDialog
          open={uploadDialogOpen}
          onClose={handleCloseUploadDialog}
          onUpload={handleThumbnailsUpload}
          totalSlides={pptxFiles.find(f => f._id === selectedFileId)?.parsedSlides?.length || 0}
        />
      )}

      <Snackbar 
        open={!!warningMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseWarning} severity="warning" sx={{ width: '100%' }}>
          {warningMessage}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Uploading PPTX snackbar */}
      <Snackbar
        open={uploadingPptx}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
          {lang === 'en' ? 'Uploading presentation...' : '正在上传演示文稿...'}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {lang === 'en' ? 'Confirm Deletion' : '确认删除'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {lang === 'en' 
              ? 'Are you sure you want to delete this presentation? This action cannot be undone. Slides using this template will no longer be able to use this template.'
              : '您确定要删除此演示文稿吗？此操作无法撤消。已经使用了此模板的幻灯片项目将无法再使用此模板。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            autoFocus
          >
            {lang === 'en' ? 'Delete' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PptxFilesPage;